import { FormikErrors, FormikProvider, useFormik } from 'formik'
import { ElementType, FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CircleCloseIcon } from '@icons/CircleCloseIcon'
import { IconProps } from '@icons/IconProps'
import { NavigationArrowDownIcon } from '@icons/NavigationArrowDownIcon'

import { Operator } from '@utils/ApiClient'
import { createAmountRangeValidator } from '@utils/validations'

import { Badge } from '@global/badge/Badge'
import { Button } from '@global/button/Button'
import { FilterQueryProps } from '@global/filter/FilterQueryModal'
import { Modal } from '@global/modal/Modal'

import { CheckBoxProps, RangeModalContent } from './components/RangeModalContent'
import { FromTo } from './interfaces/fromTo.interface'

import styles from './FilterChipDropdownList.module.scss'

interface FilterChipModalRangeProps {
  placeholders?: FromTo
  name: string
  keys: FromTo
  state?: FilterQueryProps
  isQueryParameter?: boolean
  checkBox?: CheckBoxProps
  onSelect?: (value: FilterQueryProps) => void
  inputIcon?: ElementType<IconProps>
}

export const FilterChipModalRange: FC<FilterChipModalRangeProps> = (props) => {
  const { t } = useTranslation()

  const {
    placeholders,
    name,
    keys,
    state,
    isQueryParameter,
    checkBox,
    onSelect,
    inputIcon: InputIcon,
  } = props

  const [modalOpen, setModalOpen] = useState(false)

  const text = useMemo<string>(() => {
    let count = 0

    if (state?.[keys.from]) {
      count++
    }

    if (state?.[keys.to]) {
      count++
    }

    if (checkBox && state?.[checkBox.key]) {
      count++
    }

    return count > 0 ? count.toString() : ''
  }, [checkBox, keys.from, keys.to, state])

  const getValueBasedOnQueryParameter = (value: string, operator: Operator) => {
    if (!value?.length) {
      return undefined
    }

    return isQueryParameter
      ? value
      : {
          value: value,
          operator: operator,
        }
  }

  const handleSelect = (values: FilterQueryProps) => {
    onSelect?.({
      ...state,
      [keys.from]: getValueBasedOnQueryParameter(
        values[keys.from] as string,
        Operator.MORE_OR_EQUAL
      ),
      [keys.to]: getValueBasedOnQueryParameter(values[keys.to] as string, Operator.LESS_OR_EQUAL),
      ...(checkBox
        ? {
            [checkBox.key]: values[checkBox.key],
          }
        : {}),
    })

    handleClose()
  }

  const formik = useFormik<FilterQueryProps>({
    initialValues: {
      [keys.from]: (state?.[keys.from] as string) ?? '',
      [keys.to]: (state?.[keys.to] as string) ?? '',
    },
    onSubmit: (values, { resetForm }) => {
      handleSelect(values)
      resetForm()
    },
    validate: (values) => {
      const errors: FormikErrors<FilterQueryProps> = {}
      const validateAmountRange = createAmountRangeValidator(values, errors, t)

      validateAmountRange(keys.from, keys.to)

      return errors
    },
    validateOnChange: false,
    enableReinitialize: true,
  })

  const { resetForm } = formik

  const handleClose = () => {
    setModalOpen(false)
    resetForm()
  }

  return (
    <>
      <Button.New
        renderRightIcon={() => <NavigationArrowDownIcon />}
        appearance='secondary'
        size='XS'
        onClick={() => setModalOpen(true)}
      >
        <div className={styles.filterChip}>
          {name}
          {(state?.[keys.from] || state?.[keys.to] || (checkBox && state?.[checkBox.key])) && (
            <Badge.New
              key={`${keys.from}-${keys.to}`}
              text={text}
              color='blue'
              size='small'
              rightIcon={CircleCloseIcon}
              onRightIconClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onSelect?.({
                  ...state,
                  [keys.from]: undefined,
                  [keys.to]: undefined,
                  ...(checkBox
                    ? {
                        [checkBox.key]: undefined,
                      }
                    : {}),
                })
              }}
            />
          )}
        </div>
      </Button.New>
      {modalOpen && (
        <FormikProvider value={formik}>
          <Modal
            pullDown
            closeModal={handleClose}
            render={() => (
              <RangeModalContent
                name={name}
                keys={keys}
                placeholders={placeholders}
                checkBox={checkBox}
                InputIcon={InputIcon}
                handleClose={handleClose}
              />
            )}
          />
        </FormikProvider>
      )}
    </>
  )
}
